import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import {useSelector} from 'react-redux';
import {RootState} from '../store/store';
import {setAccountBannerHeight} from '../utility/accountBannerHeight';
import axiosInstance from '../utility/axiosInstance';
import '../styles/RegistrationBilling.scss';

/** A card/budget notice has no dismiss button and remains across console routes. */
export default function RegistrationNotices() {
    const [notices,setNotices] = useState<{id:string;message:string;url:string;dismissible:boolean}[]>([]);
    const forecast=useSelector((state:RootState)=>state.billingForecast.forecast);
    const budget=useSelector((state:RootState)=>state.billingForecast.budget);
    const ref=useRef<HTMLElement>(null);
    const quietRecovery=!forecast?.paymentRecovery||['observing','recovered','deleted'].includes(forecast.paymentRecovery.phase);
    const primaryCardReminder=forecast?.policy_version===3&&forecast.tier==='trial_no_card'
        &&!forecast.suspended_by_admin&&quietRecovery;
    // The top banner's budget state (TrialBanner) asks for the budget itself, so its notice is not said twice.
    const primaryBudgetReminder=budget?.required===true&&!budget.active&&!forecast?.suspended_by_admin&&quietRecovery;
    const visible=notices.filter(n=>(!primaryCardReminder||!['add_card','free_resources_suspended'].includes(n.id))
        &&(!primaryBudgetReminder||n.id!=='set_budget'));
    useLayoutEffect(()=>{
        const clear=()=>setAccountBannerHeight('registration',0);
        if(!ref.current){clear();return clear;}
        const el=ref.current,measure=()=>setAccountBannerHeight('registration',el.offsetHeight);
        measure();const observer=new ResizeObserver(measure);observer.observe(el);
        return()=>{observer.disconnect();clear();};
    },[visible.map(n=>n.id+':'+n.message).join('|')]);
    useEffect(() => {
        let mounted = true;
        const reload = () => {
            axiosInstance.get('/v1/billing/account').then(({data}) => {
                if (mounted) setNotices(data.enrolled ? data.notices.filter((notice:any) => !notice.dismissible) : []);
            }).catch(() => { /* Preserve an existing reminder through a temporary outage. */ });
        };
        reload();
        window.addEventListener('registration-account-changed',reload);
        window.addEventListener('focus',reload);
        return () => { mounted = false; window.removeEventListener('registration-account-changed',reload); window.removeEventListener('focus',reload); };
    },[]);
    if (!visible.length) return null;
    return <section ref={ref} className="registration-billing registration-reminders" aria-label="Account setup reminders">
        {visible.map(notice => <aside key={notice.id} className="registration-billing__notice">
            {notice.message} <a href={notice.url}>{notice.id === 'add_card' ? 'Connect a card' : 'Set a budget'}</a>
        </aside>)}
    </section>;
}
